/**
 * Fleet landing page — VESSELS-ONLY SLICE (rich cards, fleet-card redesign).
 *
 * A card grid of the VESSELS the user is assigned to: one photo card per vessel.
 * Tapping a card navigates into that vessel exactly as the switcher does today
 * (setCurrentVessel + vessel lane). There is NO facility card, no facility
 * mapping, and no facility context in this build.
 *
 * PERMISSION SAFETY (FT-435 — do NOT regress):
 * Data source is the ALREADY server-scoped vessel list (VesselContext).
 * Non-admins receive only their assigned vessels from the server; admins receive
 * all org vessels. We add ZERO client-side filtering — "what the server returns
 * is what the landing shows". The attention engine is likewise handed ONLY the
 * ids in that server-scoped list, so it can never widen scope.
 *
 * RICH CARD (per Jonathan's approved mock):
 *   [photo] | Name | Type · Coding · Home port · LOA
 *           | ⚠ N maintenance jobs overdue
 *           | ⚠ N certificates expiring
 *           | Tap to open →
 * Attention flags are derived from the SAME data the live modules use (see
 * src/utils/fleetAttention.js) — read-only, sync untouched.
 *
 * PHOTO (FT-436): each card renders the vessel's PROFILE image via the reused
 * record-photo pipeline (useEntityProfilePhoto → getFullUrl). The photo is a
 * plain inert display; pic-edit lives on the 3-dot (⋯) corner icon and settings
 * on the gear icon (CardSettingsMenu), both crew-hidden. Cross-device: an
 * admin-uploaded photo pulls back on a fresh device via fleetProfilePhotoPull.
 *
 * Mobile 390x844: single-column card stack; 2-up (sm) / 3-up (lg) grid on larger
 * screens. Bottom padding clears the mobile bottom-nav.
 */

import React from 'react';
import { Ship, ChevronRight, Compass, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { useFleetAttention } from '../../../../hooks/domain/fleet/useFleetAttention';
import { usePermissions } from '../../../../hooks/usePermissions';
import { getVesselParticulars } from '../../../../utils/vesselLabels';
// FT-436: vessel profile image (upload/placeholder, synced, permission-gated) +
// per-card settings menu. Photo pipeline REUSED (see useEntityProfilePhoto).
import EntityCardPhoto from '../../../common/EntityProfilePhoto/EntityCardPhoto';
import CardSettingsMenu from '../../../common/EntityProfilePhoto/CardSettingsMenu';

// ── Attention flag rows ──────────────────────────────────────────────────────
const pluralJobs = (n) => (n === 1 ? 'maintenance job overdue' : 'maintenance jobs overdue');
const pluralCerts = (n) => (n === 1 ? 'certificate expiring' : 'certificates expiring');

const AttentionRow = ({ children, testId }) => (
  <span className="flex items-center gap-1.5 text-sm text-amber-700" data-testid={testId}>
    <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
    <span className="truncate">{children}</span>
  </span>
);

const AllClearRow = ({ testId }) => (
  <span className="flex items-center gap-1.5 text-sm text-gray-400" data-testid={testId}>
    <CheckCircle2 className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
    <span>All up to date</span>
  </span>
);

// Neutral placeholder while the (async) attention read resolves, or when a cold
// vessel could not be resolved (offline/auth/endpoint-not-yet-deployed). FT-441:
// we must NEVER show "All up to date" until we genuinely know the vessel is clear
// — a false all-clear hides overdue safety maintenance. `undefined` = still
// checking; `{unknown:true}` = resolve failed. Both render the neutral row.
const CheckingRow = ({ testId }) => (
  <span className="flex items-center gap-1.5 text-sm text-gray-400" data-testid={testId}>
    <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" aria-hidden="true" />
    <span>Checking…</span>
  </span>
);

const AttentionFlags = ({ attention, idBase }) => {
  // Not resolved yet (undefined) or resolution failed ({unknown}) → neutral state.
  if (!attention || attention.unknown) {
    return <CheckingRow testId={`${idBase}-checking`} />;
  }
  if (!attention.hasAttention) {
    return <AllClearRow testId={`${idBase}-allclear`} />;
  }
  return (
    <>
      {attention.overdueMaintenance > 0 && (
        <AttentionRow testId={`${idBase}-maintenance`}>
          {attention.overdueMaintenance} {pluralJobs(attention.overdueMaintenance)}
        </AttentionRow>
      )}
      {attention.expiringCerts > 0 && (
        <AttentionRow testId={`${idBase}-certs`}>
          {attention.expiringCerts} {pluralCerts(attention.expiringCerts)}
        </AttentionRow>
      )}
    </>
  );
};

// ── Card shell ───────────────────────────────────────────────────────────────
// FT-436 revised UX: the card is a DIV hosting nested interactive controls that
// cannot legally nest inside a button. Two separate corner icons sit in the
// top-right photo area at z-20:
//   • 3-dot (⋯)  → opens the pic-edit SetImageModal (gated, crew hidden)
//   • Cog (gear) → opens THAT vessel's settings (existing gate, crew hidden)
// The photo itself is a plain inert display — tapping it falls through to the
// z-0 full-card overlay which triggers open-vessel navigation.
const VesselCard = ({
  entity, name, particulars, attention, idBase, onClick, testId, legacyPhotoUrl,
  capturedBy, canOpenSettings, onOpenSettings,
}) => (
  <div
    data-testid={testId}
    className="group relative flex flex-col text-left bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-cyan-300 transition overflow-hidden"
  >
    {/* Corner icons (3-dot + gear) — above the photo and the open-overlay (z-20).
        CardSettingsMenu renders them as two sibling buttons; returns null for crew. */}
    <div className="absolute top-2 right-2 z-20 flex items-center gap-1">
      <CardSettingsMenu
        entityKind="vessel"
        entityId={entity.id}
        entityName={name}
        entity={entity}
        capturedBy={capturedBy}
        canOpenSettings={canOpenSettings}
        onOpenSettings={onOpenSettings}
      />
    </div>

    {/* Photo display — always inert. Tapping falls through to the z-0 overlay. */}
    <EntityCardPhoto
      entityKind="vessel"
      entityId={entity.id}
      entityName={name}
      legacyPhotoUrl={legacyPhotoUrl}
      fallbackIcon={Ship}
      capturedBy={capturedBy}
    />

    <div className="flex-1 flex flex-col gap-2 p-4">
      <span className="block font-semibold text-gray-900 truncate">{name}</span>
      {particulars ? (
        <span className="block text-xs text-gray-500 truncate" data-testid={`${idBase}-particulars`}>
          {particulars}
        </span>
      ) : (
        <span className="block text-xs text-gray-300" data-testid={`${idBase}-particulars`}>
          No particulars set
        </span>
      )}
      <span className="mt-1 flex flex-col gap-1 border-t border-gray-100 pt-2">
        <AttentionFlags attention={attention} idBase={idBase} />
      </span>
      {/* Open-navigation control. Placed above the overlay in z-order so it is
          directly clickable; the full-card overlay below catches taps on inert
          areas (name/particulars) for a large tap target on mobile. */}
      <button
        type="button"
        onClick={onClick}
        className="relative z-10 mt-1 flex items-center gap-1 text-xs font-medium text-cyan-600 group-hover:text-cyan-700 self-start"
        data-testid={`${idBase}-open`}
      >
        Tap to open <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </div>

    {/* Full-card open overlay: covers the card so tapping anywhere non-interactive
        opens the vessel. Corner icons sit at z-20 and stopPropagation so icon taps
        do NOT also trigger navigation. Photo is inert — tap falls through to here. */}
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${name}`}
      className="absolute inset-0 z-0"
      tabIndex={-1}
      data-testid={`${idBase}-open-overlay`}
    />
  </div>
);

// ── Section grid ─────────────────────────────────────────────────────────────
const SectionHeading = ({ children }) => (
  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{children}</h2>
);

const CardGrid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
);

const VesselSection = ({ vessels, attentionById, onSelectVessel, capturedBy, canOpenSettings, onOpenVesselSettings }) => (
  <section>
    <SectionHeading>Vessels ({vessels.length})</SectionHeading>
    <CardGrid>
      {vessels.map((v) => (
        <VesselCard
          key={v.id}
          entity={v}
          legacyPhotoUrl={v.photoUrl || null}
          name={v.name || 'Unnamed vessel'}
          particulars={getVesselParticulars(v).join(' · ')}
          attention={attentionById[v.id]}
          idBase={`fleet-card-vessel-${v.id}`}
          onClick={() => onSelectVessel && onSelectVessel(v.id)}
          testId={`fleet-card-vessel-${v.id}`}
          capturedBy={capturedBy}
          canOpenSettings={canOpenSettings}
          onOpenSettings={onOpenVesselSettings}
        />
      ))}
    </CardGrid>
  </section>
);

const EmptyState = () => (
  <div className="text-center py-16 bg-white border border-gray-200 rounded-xl" data-testid="fleet-landing-empty">
    <Compass className="w-12 h-12 text-gray-300 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">No vessels assigned yet</h3>
    <p className="text-gray-500">Contact your administrator to be assigned a vessel.</p>
  </div>
);

/**
 * @param {(vesselId: string) => void} onSelectVessel  navigate into a vessel
 * @param {(vessel: object) => void} [onOpenVesselSettings]  FT-436 per-card
 *   settings shortcut — open THAT vessel's settings directly.
 */
const FleetLanding = ({ onSelectVessel, onOpenVesselSettings }) => {
  // enrichedVessels (FT-231) carries the per-vessel settings merged in — type,
  // category, homePort, lengthOverall — which the bare `vessels` array (loaded
  // once at boot) does NOT hold. Particulars (Belfast · 14.9m LOA) only exist on
  // the enriched list, so the card MUST read it. enrichedVessels is derived 1:1
  // from the same server-scoped `vessels` list (VesselContext), so this does NOT
  // change WHICH vessels render — FT-435 permission-scoping is unchanged.
  const { enrichedVessels } = useVessel();
  // FT-436: capturedBy stamps the profile-photo audit row; canOpenSettings gates
  // the per-card gear using the SAME rule the sidebar uses (can('update','vessel')).
  const { currentUser, can } = usePermissions();
  const capturedBy = currentUser?.id || 'unknown';
  const canOpenSettings = can('update', 'vessel');

  const vesselList = enrichedVessels || [];
  const isEmpty = vesselList.length === 0;

  // FT-441: attention flags are resolved asynchronously (batched read-only server
  // pull) so cold, never-opened vessels report the TRUE overdue/expiry counts —
  // not a false "all up to date". The hook is handed ONLY the server-scoped ids
  // this list already contains (FT-435 unchanged).
  const { vesselAttention } = useFleetAttention(vesselList.map((v) => v.id));

  return (
    // pb-24 clears the mobile bottom-nav; sm:pb-6 restores normal padding once the
    // bottom-nav is gone (tablet/desktop).
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24 sm:pb-6" data-testid="fleet-landing">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Your Fleet</h1>
        <p className="text-sm text-gray-500 mt-1">Choose a vessel to get started.</p>
      </header>

      {isEmpty ? (
        <EmptyState />
      ) : (
        <div className="space-y-8">
          <VesselSection
            vessels={vesselList}
            attentionById={vesselAttention}
            onSelectVessel={onSelectVessel}
            capturedBy={capturedBy}
            canOpenSettings={canOpenSettings}
            onOpenVesselSettings={onOpenVesselSettings}
          />
        </div>
      )}
    </div>
  );
};

export default FleetLanding;
